import React, { useEffect, useMemo, useState } from "react";
import API from "../../api";
import "../css/TicketDetailDrawer.css";

function formatDateTime(value) {
  if (!value) return "—";
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  try {
    return d.toLocaleString(undefined, {
      year: "numeric", month: "short", day: "2-digit",
      hour: "2-digit", minute: "2-digit",
    });
  } catch {
    return String(value);
  }
}

function categoryLabel(v) {
  const s = String(v || "");
  const map = {
    technical_issue: "Technical issue",
    customer_service: "Customer Service",
    bank_info_change: "Bank Info Change",
    setup_call: "Setup call",
  };
  return map[s] || s || "—";
}

function Field({ label, value }) {
  return (
    <div className="td-row">
      <div className="td-label">{label}</div>
      <div className="td-value">{value ?? "—"}</div>
    </div>
  );
}

export default function TicketDetailDrawer({
  open,
  ticketId,
  onClose,
  currentRole,
  currentUsername,
  onChanged,
}) {
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [ticket, setTicket] = useState(null);
  const [comments, setComments] = useState([]);

  // editable fields
  const [newStatus, setNewStatus] = useState("open");
  const [commentText, setCommentText] = useState("");
  const [callDate, setCallDate] = useState("");
  const [callTime, setCallTime] = useState("");

  // tracking (only for "Tracking ID Status" ticket)
  const [tracking, setTracking] = useState("");
  const [trackingLoading, setTrackingLoading] = useState(false);
  const [trackingSaving, setTrackingSaving] = useState(false);
  const [trackingErr, setTrackingErr] = useState("");

  // single saving flag
  const [saving, setSaving] = useState(false);

  const canModerate = useMemo(
    () =>
      String(currentRole || "").toLowerCase() === "admin" ||
      String(currentRole || "").toLowerCase() === "sub-admin",
    [currentRole]
  );

  async function load() {
    if (!ticketId) return;
    setLoading(true);
    setErr("");
    try {
      const resp = await API.get(`/auth/ticket/${ticketId}`);
      const t = resp?.data?.ticket || null;
      setTicket(t);
      setNewStatus(t?.status || "open");
      setCallDate(t?.call_date ? String(t.call_date).slice(0, 10) : "");
      setCallTime(t?.call_time ? String(t.call_time).slice(0, 5) : "");

      if (t && String(t.title || "").trim() === "Tracking ID Status") {
        await loadTracking(t.merchant_id);
      } else {
        setTracking("");
        setTrackingErr("");
      }

      setComments(Array.isArray(resp?.data?.comments) ? resp.data.comments : []);
    } catch (e) {
      console.error(e);
      setErr(e?.response?.data?.error || "Could not load ticket.");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    if (!open) return;
    setCommentText("");
    setCallDate("");
    setCallTime("");
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, ticketId]);

  if (!open) return null;

  // ── Single Update handler ─────────────────────────────────────────────────
  async function handleUpdate() {
    if (!ticketId || !canModerate) return;
    setSaving(true);
    try {
      const prevStatus = ticket?.status;

      // 1. Save status
      await API.post("/auth/ticket-status", {
        role: currentRole,
        username: currentUsername,
        ticket_id: ticketId,
        status: newStatus,
      });

      // 2. Save call date/time
      await API.post("/auth/ticket-call-datetime", {
        role: currentRole,
        username: currentUsername,
        ticket_id: ticketId,
        call_date: callDate || null,
        call_time: callTime || null,
      });

      // 3. Add comment only if not blank
      if (commentText.trim()) {
        await API.post("/auth/ticket-comment", {
          role: currentRole,
          username: currentUsername,
          ticket_id: ticketId,
          comment: commentText.trim(),
        });
        setCommentText("");
      }

      // 4. Auto-create PCI Due ticket when "PCI Compliance status" is closed
      if (
        String(ticket?.title || "").trim() === "PCI Compliance status" &&
        newStatus === "close" &&
        prevStatus !== "close"
      ) {
        const pciDate = new Date();
        pciDate.setMonth(pciDate.getMonth() + 11);
        const pciCallDate = pciDate.toISOString().slice(0, 10);

        const createResp = await API.post("/auth/ticket-create", {
          role: currentRole,
          username: currentUsername,
          merchant_id: ticket.merchant_id,
          title: "PCI Due",
          category: "pci_due",
          description: ticket.description || "",
        });

        const newTicketId = createResp?.data?.ticket_id;
        if (newTicketId) {
          await API.post("/auth/ticket-call-datetime", {
            role: currentRole,
            username: currentUsername,
            ticket_id: newTicketId,
            call_date: pciCallDate,
            call_time: null,
          });
        }
      }

      await load();
      onChanged?.();
    } catch (e) {
      console.error(e);
      alert(e?.response?.data?.error || "Update failed.");
    } finally {
      setSaving(false);
    }
  }
  // ─────────────────────────────────────────────────────────────────────────

  const isTrackingTicket = String(ticket?.title || "").trim() === "Tracking ID Status";

  async function loadTracking(merchantId) {
    if (!merchantId) return;
    setTrackingErr("");
    setTrackingLoading(true);
    try {
      const resp = await API.get(`/auth/mpa-tracking/${merchantId}`);
      setTracking(resp?.data?.tracking || "");
    } catch (e) {
      console.error(e);
      setTrackingErr(e?.response?.data?.error || "Could not load tracking.");
    } finally {
      setTrackingLoading(false);
    }
  }

  async function saveTracking() {
    const merchantId = ticket?.merchant_id;
    if (!merchantId) return;
    try {
      setTrackingSaving(true);
      setTrackingErr("");
      await API.post("/auth/mpa-tracking", { merchant_id: merchantId, tracking });
      await loadTracking(merchantId);
      onChanged?.();
    } catch (e) {
      console.error(e);
      setTrackingErr(e?.response?.data?.error || "Tracking update failed.");
    } finally {
      setTrackingSaving(false);
    }
  }

  return (
    <div className="td-backdrop" onClick={onClose} role="presentation">
      <div className="td-drawer td-open" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">

        {/* ── Header ── */}
        <div className="td-header">
          <div>
            <div className="td-title">Ticket #{ticketId}</div>
            <div className="td-sub">
              Merchant: <b>{ticket?.merchant_id || "—"}</b> &nbsp;•&nbsp; Created By: <b>{ticket?.created_by || "—"}</b>
              <br />
              Created At: <b>{formatDateTime(ticket?.created_at) || "—"}</b>
            </div>
          </div>
          <button className="td-close" onClick={onClose} aria-label="Close">✕</button>
        </div>

        {/* ── Body (scrollable) ── */}
        <div className="td-body">
          {loading && <div className="td-loading">Loading…</div>}
          {err && <div className="td-error">{err}</div>}

          {!loading && !err && ticket && (
            <div className="td-body-inner">

              {/* Left column */}
              <div className="td-col-left">
                <div className="td-section-title">Ticket Details</div>
                <div className="td-grid">
                  <Field label="Title" value={ticket.title} />
                  <Field label="Category" value={categoryLabel(ticket.category)} />
                  <Field label="Status" value={ticket.status} />
                  <Field label="Updated At" value={formatDateTime(ticket.updated_at)} />
                </div>

                <div className="td-desc">
                  <div className="td-desc-label">Description</div>
                  <div className="td-desc-box">{ticket.description || "—"}</div>
                </div>

                {isTrackingTicket && (
                  <div className="td-tracking">
                    <div className="td-desc-label">Tracking ID</div>
                    {trackingErr && <div className="td-error">{trackingErr}</div>}
                    <input
                      className="td-tracking-input"
                      type="text"
                      value={tracking}
                      onChange={(e) => setTracking(e.target.value)}
                      placeholder={trackingLoading ? "Loading…" : "Enter Tracking ID"}
                      disabled={!canModerate || trackingLoading}
                    />
                    {canModerate && (
                      <button
                        className="td-btn td-primary"
                        onClick={saveTracking}
                        disabled={trackingSaving || trackingLoading}
                        style={{ marginTop: 8 }}
                      >
                        {trackingSaving ? "Saving…" : "Save Tracking"}
                      </button>
                    )}
                  </div>
                )}
              </div>

              {/* Right column — comments */}
              <div className="td-col-right">
                <div className="td-section-title">Comments</div>
                {comments.length === 0 ? (
                  <div className="td-empty">No comments yet.</div>
                ) : (
                  <div className="td-comments">
                    {comments.map((c) => (
                      <div key={c.id} className="td-comment">
                        <div className="td-comment-top">
                          <div className="td-comment-by">{c.commented_by || "—"}</div>
                          <div className="td-comment-time">{formatDateTime(c.created_at)}</div>
                        </div>
                        <div className="td-comment-text">{c.comment}</div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

            </div>
          )}
        </div>

        {/* ── Footer ── */}
        <div className="td-footer">
          <div className="td-footer-grid">
            {/* Row 1: Status | Call Date | Call Time */}
            <div className="td-field">
              <label>Status</label>
              <select value={newStatus} onChange={(e) => setNewStatus(e.target.value)} disabled={!canModerate}>
                <option value="open">Open</option>
                <option value="close">Close</option>
              </select>
            </div>

            <div className="td-field">
              <label>Call Date</label>
              <input
                type="date"
                className="td-date-input"
                value={callDate}
                onChange={(e) => setCallDate(e.target.value)}
                disabled={!canModerate}
              />
            </div>

            <div className="td-field">
              <label>Call Time</label>
              <input
                type="time"
                className="td-date-input"
                value={callTime}
                onChange={(e) => setCallTime(e.target.value)}
                disabled={!canModerate}
              />
            </div>

            {/* Row 2: Comment (spans all 3) */}
            <div className="td-field td-span-3">
              <label>Add comment {!commentText.trim() && <span className="td-optional">(optional)</span>}</label>
              <textarea
                rows={3}
                value={commentText}
                onChange={(e) => setCommentText(e.target.value)}
                placeholder={canModerate ? "Write a comment… (leave blank to skip)" : "Only admin/sub-admin can comment"}
                disabled={!canModerate}
              />
            </div>
          </div>

          {/* Actions — only 2 buttons */}
          <div className="td--actions">
            <button className="td-btn" onClick={onClose}>Close</button>
            <button
              className="td-btn td-primary"
              onClick={handleUpdate}
              disabled={!canModerate || saving}
            >
              {saving ? "Updating…" : "Update"}
            </button>
          </div>
        </div>

      </div>
    </div>
  );
}
